<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import debounce from 'lodash.debounce'

import { usePreferenceForm } from '@/stores/preference-form'
import { usePublic } from '@/stores/public'
import { TEMPLATES } from '@/utils/defaults'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const storePublic = usePublic()

const customerIdentifier = ref({})
const customerId = ref(null)
const email = ref(null)
const challenge = ref(null)
const saved = ref(false)
const saveStatus = ref('idle')
const { locale } = useI18n()

const currentStep = ref('form')
const PREVIEW_EMAIL = 'user@example.com'
const isPreviewMode = ref(false)

const isValidEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    )
}

fetchIdentifier()
if (email.value === PREVIEW_EMAIL) {
  isPreviewMode.value = true
  loadPreviewMode()
} else if (Object.keys(customerIdentifier.value).length != 0) {
  submitCustomer(customerIdentifier.value)
}

async function loadPreviewMode() {
  try {
    await storePublic.getPreferences(route.params.customPath)
    await setIcons()
    storePublic.preferences.lists.forEach((list) => {
      if (list.type !== 'separator') {
        list.subscribed = true
      }
    })
    storePublic.customer = { email: PREVIEW_EMAIL, global_unsubscribe: false }
    currentStep.value = 'preferences'
    history.pushState({}, null, `${route.path}`)
  } catch {
    window.location.href = '/404'
  }
}

function setLang() {
  const enabledLanguages = storePublic.preferences.languages
  if (!enabledLanguages.default) {
    locale.value = 'en'
    return
  }

  locale.value = enabledLanguages.default
  window.navigator.languages.some((user_locale) => {
    if (user_locale.includes(enabledLanguages.default)) {
      locale.value = enabledLanguages.default
      return true
    }

    let langFound = enabledLanguages.active_languages?.some((lang) => {
      if (user_locale.includes(lang)) {
        locale.value = user_locale
        return true
      }
    })
    if (langFound) {
      return true
    }
  })
}

const setIcons = async () => {
  const favicon = document.getElementById('favicon')
  favicon.href = storePublic.preferences.favicon || '/favicon.png'
  document.getElementById('page_title').innerHTML = storePublic.preferences.name
  setLang()
}
// Fetch preferences asynchronously
const fetchPreferences = async () => {
  if (Object.keys(customerIdentifier.value).length != 0) {
    return
  }

  try {
    await storePublic.getPreferences(route.params.customPath)
    await setIcons()
  } catch (error) {
    window.location.href = '/404'
  }
}

function fetchIdentifier() {
  if (route.query?.challenge || localStorage.getItem('challenge')) {
    challenge.value = route.query?.challenge || localStorage.getItem('challenge')
    customerIdentifier.value.challenge = challenge.value
    window.localStorage.setItem('challenge', challenge.value)
  } else if (route.query?.email || window.localStorage.getItem('email')) {
    email.value = route.query?.email || window.localStorage.getItem('email')
    if (isValidEmail(email.value)) {
      customerIdentifier.value.email = email.value
      if (email.value !== PREVIEW_EMAIL) {
        window.localStorage.setItem('email', email.value)
      }
    }
  }
}

async function submitCustomer(customerIdentifier) {
  if (isPreviewMode.value) {
    currentStep.value = 'preferences'
    return
  }
  try {
    const promise = await storePublic
      .getOrCreateCustomer({
        ...customerIdentifier,
        custom_path: route.params.customPath,
      })
      .then((resp) => {
        email.value = resp.email
        window.localStorage.setItem('email', email.value)
        return resp
      })

    await storePublic.getCustomerPreferences(promise.id, route.params.customPath)
    await setIcons()
    customerId.value = promise.id || ''

    if (route.query?.state && ['unsubscribe', 'subscribe'].includes(route.query.state)) {
      const findByParam = await import('@/utils/findByParam')

      if (route.query.listId && findByParam(storePublic.preferences.lists, 'id', route.query.listId)) {
        storePublic.updateListValue(route.query.listId, route.query.state === 'unsubscribe' ? false : true)
      } else {
        storePublic.customer.global_unsubscribe = route.query.state === 'unsubscribe' ? true : false
      }

      savePreferences()
    }

    history.pushState({}, null, `${route.path}`)
    currentStep.value = 'preferences'
  } catch (error) {
    window.location.href = '/404'
  }
}

const preferenceForm = usePreferenceForm()

const debouncedSave = debounce(() => {
  savePreferences()
}, 600)

function updateLists(lists) {
  preferenceForm.lists = lists
  if (storePublic.preferences.attrs?.auto_save) {
    debouncedSave()
  }
}

async function savePreferences() {
  if (isPreviewMode.value) {
    if (storePublic.preferences.attrs?.auto_save) {
      saveStatus.value = 'saving'
      setTimeout(() => {
        saveStatus.value = 'saved'
        setTimeout(() => {
          saveStatus.value = 'idle'
        }, 1500)
      }, 300)
    } else {
      openPopUp()
    }
    return
  }

  const isAutoSave = storePublic.preferences.attrs?.auto_save

  if (isAutoSave) {
    saveStatus.value = 'saving'
  }

  const lists = []

  storePublic.preferences.lists
    .filter((item) => item.type !== 'separator')
    .forEach((item) => {
      lists.push({
        notification_list_id: item.id,
        subscribed: item.subscribed,
      })
    })

  await storePublic.updateCustomerPreferences({
    id: customerId.value,
    preferences: lists,
  })

  await storePublic.updateCustomerGlobalUnsubscribe({
    id: customerId.value,
    global_unsubscribe: storePublic.customer.global_unsubscribe,
  })

  if (isAutoSave) {
    saveStatus.value = 'saved'
    setTimeout(() => {
      saveStatus.value = 'idle'
    }, 1500)
  } else {
    openPopUp()
  }
}

function openPopUp() {
  saved.value = true
  setTimeout(() => {
    saved.value = false
  }, 3000)
}

fetchPreferences().then(() => {
  if (!isPreviewMode.value && storePublic.preferences.attrs?.enhanced_security == true && email.value == null && challenge.value == null) {
    window.location.href = '/404'
  }
})
</script>

<template>
  <main class="flex w-screen col-span-2 wrapper justify-center align-center">
    <div v-if="storePublic.isLoading" class="flex items-center min-h-screen">
      <MainLoader />
    </div>
    <component
      :is="TEMPLATES[storePublic.preferences.attrs?.template_id || 'default_preference_page']"
      v-else
      :email="email"
      :customer-id="customerId"
      :name="storePublic.preferences.name"
      :lists="storePublic.preferences.lists"
      :saved="saved"
      :save-status="saveStatus"
      :logo="storePublic.preferences.logo"
      :current-step="currentStep"
      :attrs="storePublic.preferences.attrs"
      @user-email="submitCustomer"
      @save="savePreferences"
      @update="updateLists"
    />
  </main>
</template>
